import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, TrendingUp, Users, MapPin, Search, Calendar, Star, Home, BarChart3, Globe, Shield, Eye } from 'lucide-react';
import StateSelector from '../components/StateSelector';

const playfair = { fontFamily: "'Playfair Display', Georgia, serif" };

/* ─── Synthetic Data ─── */
const MONTHLY_SEARCHES = [
  { month: 'Jan', searches: 12400, bookings: 1860 },
  { month: 'Feb', searches: 14200, bookings: 2130 },
  { month: 'Mar', searches: 18900, bookings: 3024 },
  { month: 'Apr', searches: 22100, bookings: 3978 },
  { month: 'May', searches: 26800, bookings: 4824 },
  { month: 'Jun', searches: 31200, bookings: 5304 },
  { month: 'Jul', searches: 28400, bookings: 4544 },
  { month: 'Aug', searches: 24600, bookings: 3444 },
  { month: 'Sep', searches: 29800, bookings: 5066 },
  { month: 'Oct', searches: 34200, bookings: 6156 },
  { month: 'Nov', searches: 21600, bookings: 3456 },
  { month: 'Dec', searches: 27400, bookings: 4932 },
];

const TOP_SEARCHES = [
  { query: 'homestay near Manali with mountain view', count: 4820, trend: '+23%' },
  { query: 'riverside cottage Tirthan Valley', count: 3640, trend: '+41%' },
  { query: 'budget homestay Kasol', count: 3210, trend: '-8%' },
  { query: 'luxury stay Shimla heritage', count: 2890, trend: '+17%' },
  { query: 'offbeat homestay Spiti Valley', count: 2740, trend: '+62%' },
  { query: 'family stay Dalhousie with kids', count: 2380, trend: '+34%' },
  { query: 'treehouse stay Jibhi', count: 2150, trend: '+89%' },
  { query: 'snow view cottage Kufri', count: 1920, trend: '+12%' },
];

const ORIGIN_CITIES = [
  { city: 'Delhi NCR', pct: 34 },
  { city: 'Mumbai', pct: 18 },
  { city: 'Bangalore', pct: 14 },
  { city: 'Chandigarh', pct: 11 },
  { city: 'Kolkata', pct: 7 },
  { city: 'Pune', pct: 6 },
  { city: 'Hyderabad', pct: 5 },
  { city: 'Others', pct: 5 },
];

const STATE_COVERAGE = [
  { state: 'Himachal Pradesh', total: 3790, verified: 2840, gold: 48, occupancy: 72 },
  { state: 'Uttarakhand', total: 3550, verified: 2490, gold: 35, occupancy: 68 },
  { state: 'J&K', total: 2800, verified: 1680, gold: 22, occupancy: 64 },
  { state: 'Sikkim', total: 1900, verified: 1235, gold: 18, occupancy: 71 },
  { state: 'Assam', total: 1580, verified: 790, gold: 12, occupancy: 58 },
  { state: 'West Bengal', total: 1215, verified: 730, gold: 14, occupancy: 66 },
  { state: 'Meghalaya', total: 1260, verified: 630, gold: 8, occupancy: 61 },
  { state: 'Arunachal', total: 1550, verified: 620, gold: 6, occupancy: 54 },
  { state: 'Ladakh', total: 1200, verified: 840, gold: 16, occupancy: 76 },
  { state: 'Manipur', total: 1220, verified: 488, gold: 5, occupancy: 48 },
  { state: 'Nagaland', total: 1190, verified: 476, gold: 7, occupancy: 52 },
  { state: 'Mizoram', total: 990, verified: 396, gold: 4, occupancy: 45 },
  { state: 'Tripura', total: 910, verified: 364, gold: 3, occupancy: 42 },
];

const SEASONAL_HEATMAP = [
  { valley: 'Manali / Kullu', jan: 45, feb: 48, mar: 62, apr: 78, may: 92, jun: 88, jul: 72, aug: 65, sep: 82, oct: 95, nov: 58, dec: 85 },
  { valley: 'Tirthan Valley', jan: 32, feb: 38, mar: 55, apr: 72, may: 88, jun: 82, jul: 60, aug: 52, sep: 78, oct: 90, nov: 48, dec: 42 },
  { valley: 'Spiti Valley', jan: 15, feb: 12, mar: 18, apr: 35, may: 68, jun: 92, jul: 85, aug: 78, sep: 88, oct: 72, nov: 28, dec: 18 },
  { valley: 'Shimla / Kufri', jan: 55, feb: 52, mar: 58, apr: 65, may: 78, jun: 72, jul: 58, aug: 55, sep: 68, oct: 82, nov: 62, dec: 75 },
  { valley: 'Dharamshala', jan: 42, feb: 45, mar: 58, apr: 72, may: 82, jun: 78, jul: 55, aug: 48, sep: 72, oct: 88, nov: 52, dec: 48 },
  { valley: 'Darjeeling', jan: 38, feb: 42, mar: 55, apr: 68, may: 72, jun: 52, jul: 38, aug: 35, sep: 58, oct: 82, nov: 65, dec: 55 },
  { valley: 'Kaziranga', jan: 62, feb: 68, mar: 75, apr: 58, may: 25, jun: 10, jul: 8, aug: 12, sep: 18, oct: 45, nov: 72, dec: 68 },
  { valley: 'Gulmarg', jan: 82, feb: 78, mar: 65, apr: 55, may: 68, jun: 75, jul: 58, aug: 52, sep: 72, oct: 78, nov: 58, dec: 88 },
];

const MONTHS_SHORT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

const TRAVELLER_TYPES = [
  { type: 'Couples', pct: 34, color: '#9EBD6E' },
  { type: 'Families', pct: 28, color: '#C4915C' },
  { type: 'Solo', pct: 22, color: '#5FB8C2' },
  { type: 'Groups', pct: 16, color: '#9B7EC8' },
];

const QUALITY_METRICS = [
  { label: '5 Star', count: 2840, pct: 24 },
  { label: '4 Star', count: 4680, pct: 39 },
  { label: '3 Star', count: 2960, pct: 25 },
  { label: '2 Star', count: 1080, pct: 9 },
  { label: '1 Star', count: 360, pct: 3 },
];

/* ─── AnimatedNumber ─── */
function AnimatedNumber({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}{suffix}</span>;
}

/* ─── Bar Chart ─── */
function BarChart({ data, maxVal }) {
  return (
    <div className="flex items-end gap-1.5 h-40">
      {data.map((d, i) => {
        const h = (d.searches / maxVal) * 100;
        const hb = (d.bookings / maxVal) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
            <div className="w-full flex flex-col items-center gap-0.5" style={{ height: '140px', justifyContent: 'flex-end' }}>
              <div className="w-full rounded-t bg-white/8 group-hover:bg-white/15 transition-colors relative" style={{ height: `${h}%` }}>
                <div className="absolute bottom-0 left-0 right-0 rounded-t bg-[#9EBD6E]/60 group-hover:bg-[#9EBD6E]/80 transition-colors" style={{ height: `${hb}%` }} />
              </div>
            </div>
            <span className="text-white/30 text-[9px]">{d.month}</span>
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#1a2430] border border-white/10 rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              <p className="text-white text-[10px]">{d.searches.toLocaleString()} searches</p>
              <p className="text-[#9EBD6E] text-[10px]">{d.bookings.toLocaleString()} bookings</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Heatmap Cell ─── */
function HeatCell({ value }) {
  const opacity = Math.max(0.08, value / 100);
  return (
    <div
      className="w-full aspect-square rounded-sm flex items-center justify-center text-[8px] cursor-default group relative"
      style={{ backgroundColor: `rgba(158,189,110,${opacity})` }}
    >
      <span className="text-white/0 group-hover:text-white/90 transition-colors">{value}%</span>
    </div>
  );
}

export default function StateAnalytics() {
  const maxSearches = Math.max(...MONTHLY_SEARCHES.map(d => d.searches));
  const totalSearches = MONTHLY_SEARCHES.reduce((s, d) => s + d.searches, 0);
  const totalBookings = MONTHLY_SEARCHES.reduce((s, d) => s + d.bookings, 0);
  const totalHomestays = STATE_COVERAGE.reduce((s, d) => s + d.total, 0);
  const totalVerified = STATE_COVERAGE.reduce((s, d) => s + d.verified, 0);

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="state-analytics-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1419]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="analytics" />
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6" data-testid="analytics-hero">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 border border-[#9EBD6E]/30 bg-[#9EBD6E]/5 px-4 py-1.5 rounded-full">
              <div className="w-2 h-2 rounded-full bg-[#9EBD6E] animate-pulse" />
              <span className="text-[#9EBD6E] text-[10px] tracking-[0.3em] uppercase">Live Dashboard Preview</span>
            </div>
            <span className="text-white/20 text-[10px] tracking-wider">SYNTHETIC DATA</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl text-white font-light italic tracking-tight mb-4" style={playfair}>
            State Tourism Intelligence
          </h1>
          <p className="text-white/40 text-sm sm:text-base max-w-2xl leading-relaxed mb-12">
            A preview of the real-time analytics dashboard every partner state receives — traveller demand signals,
            seasonal patterns, origin-city mapping, and quality metrics across the entire Himalayan homestay ecosystem.
          </p>

          {/* KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="kpi-strip">
            {[
              { label: 'Total Homestays Mapped', value: totalHomestays, suffix: '+', icon: Home, color: '#9EBD6E' },
              { label: 'Quality Verified', value: totalVerified, suffix: '+', icon: Shield, color: '#5FB8C2' },
              { label: 'Annual Searches', value: totalSearches, suffix: '', icon: Search, color: '#C4915C' },
              { label: 'Annual Bookings', value: totalBookings, suffix: '', icon: TrendingUp, color: '#9B7EC8' },
            ].map(kpi => (
              <div key={kpi.label} className="border border-white/8 rounded-xl p-5 bg-white/[0.02]" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                <kpi.icon className="w-4 h-4 mb-3" style={{ color: kpi.color }} />
                <p className="text-2xl sm:text-3xl text-white font-light mb-1" style={playfair}>
                  <AnimatedNumber target={kpi.value} suffix={kpi.suffix} />
                </p>
                <p className="text-white/30 text-[10px] tracking-wider uppercase">{kpi.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search Demand Trends */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="demand-trends">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Chart */}
            <div className="lg:col-span-2 border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Demand Intelligence</p>
                  <h3 className="text-xl text-white font-light italic" style={playfair}>Monthly Search & Booking Trends</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-white/10" /><span className="text-white/30 text-[10px]">Searches</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-[#9EBD6E]/60" /><span className="text-white/30 text-[10px]">Bookings</span></div>
                </div>
              </div>
              <BarChart data={MONTHLY_SEARCHES} maxVal={maxSearches} />
              <div className="mt-4 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#9EBD6E]" />
                <span className="text-[#9EBD6E] text-xs">+28% YoY growth in search demand</span>
              </div>
            </div>

            {/* Top Searches */}
            <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="mb-5">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">What Travellers Search</p>
                <h3 className="text-lg text-white font-light italic" style={playfair}>Top Search Queries</h3>
              </div>
              <div className="space-y-3">
                {TOP_SEARCHES.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <span className="text-white/15 text-xs mt-0.5 w-4 flex-shrink-0">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/60 text-xs leading-relaxed truncate group-hover:text-white/80 transition-colors">{s.query}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-white/25 text-[10px]">{s.count.toLocaleString()}</span>
                        <span className={`text-[10px] ${s.trend.startsWith('+') ? 'text-[#9EBD6E]' : 'text-red-400/70'}`}>{s.trend}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Heatmap */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="seasonal-heatmap">
        <div className="max-w-6xl mx-auto">
          <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
            <div className="mb-6">
              <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Seasonal Intelligence</p>
              <h3 className="text-xl text-white font-light italic" style={playfair}>Valley-wise Occupancy Heatmap</h3>
              <p className="text-white/25 text-xs mt-1">Hover cells to see occupancy percentage</p>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[640px]">
                <div className="grid gap-1" style={{ gridTemplateColumns: '160px repeat(12, 1fr)' }}>
                  <div />
                  {MONTHS_SHORT.map(m => (
                    <div key={m} className="text-center text-white/25 text-[9px] tracking-wider py-1">{m}</div>
                  ))}
                  {SEASONAL_HEATMAP.map(row => (
                    <React.Fragment key={row.valley}>
                      <div className="text-white/50 text-[11px] flex items-center pr-3 truncate">{row.valley}</div>
                      <HeatCell value={row.jan} /><HeatCell value={row.feb} /><HeatCell value={row.mar} />
                      <HeatCell value={row.apr} /><HeatCell value={row.may} /><HeatCell value={row.jun} />
                      <HeatCell value={row.jul} /><HeatCell value={row.aug} /><HeatCell value={row.sep} />
                      <HeatCell value={row.oct} /><HeatCell value={row.nov} /><HeatCell value={row.dec} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <span className="text-white/20 text-[9px]">Low</span>
              <div className="flex gap-0.5">
                {[0.08, 0.2, 0.35, 0.5, 0.65, 0.8, 0.95].map(o => (
                  <div key={o} className="w-5 h-3 rounded-sm" style={{ backgroundColor: `rgba(158,189,110,${o})` }} />
                ))}
              </div>
              <span className="text-white/20 text-[9px]">High</span>
            </div>
          </div>
        </div>
      </section>

      {/* Traveller Demographics + Origin Cities */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="traveller-insights">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Origin Cities */}
            <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="mb-6">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Origin Mapping</p>
                <h3 className="text-xl text-white font-light italic" style={playfair}>Where Travellers Come From</h3>
              </div>
              <div className="space-y-3">
                {ORIGIN_CITIES.map(c => (
                  <div key={c.city} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/60 text-sm group-hover:text-white/80 transition-colors">{c.city}</span>
                      <span className="text-white/30 text-xs">{c.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#9EBD6E] to-[#9EBD6E]/40 rounded-full transition-all duration-1000" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Traveller Types */}
            <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="mb-6">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Traveller Segments</p>
                <h3 className="text-xl text-white font-light italic" style={playfair}>Who's Travelling</h3>
              </div>
              <div className="flex items-center justify-center mb-8">
                <div className="relative w-44 h-44">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    {(() => {
                      let offset = 0;
                      return TRAVELLER_TYPES.map(t => {
                        const dash = t.pct;
                        const gap = 100 - dash;
                        const el = (
                          <circle key={t.type} cx="18" cy="18" r="15.5" fill="none" stroke={t.color} strokeWidth="3"
                            strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset} strokeLinecap="round" />
                        );
                        offset += dash;
                        return el;
                      });
                    })()}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-2xl text-white font-light" style={playfair}>100%</p>
                    <p className="text-white/25 text-[9px] tracking-wider uppercase">Segments</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {TRAVELLER_TYPES.map(t => (
                  <div key={t.type} className="flex items-center gap-2.5 border border-white/5 rounded-lg p-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                    <div>
                      <p className="text-white/70 text-sm">{t.type}</p>
                      <p className="text-white/30 text-xs">{t.pct}%</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Avg Stay + Avg Spend */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-2xl text-white font-light" style={playfair}>2.8</p>
                  <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Avg nights</p>
                </div>
                <div className="border border-white/5 rounded-lg p-4 text-center">
                  <p className="text-2xl text-white font-light" style={playfair}>4,200</p>
                  <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Avg spend / night</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Coverage Table */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="state-coverage">
        <div className="max-w-6xl mx-auto">
          <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Digital Registry</p>
                <h3 className="text-xl text-white font-light italic" style={playfair}>State-wise Coverage & Quality</h3>
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-[#9EBD6E]" />
                <span className="text-[#9EBD6E] text-xs">13 states live</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-white/30 text-[10px] tracking-wider uppercase py-3 pr-4">State</th>
                    <th className="text-right text-white/30 text-[10px] tracking-wider uppercase py-3 px-4">Total Mapped</th>
                    <th className="text-right text-white/30 text-[10px] tracking-wider uppercase py-3 px-4">Verified</th>
                    <th className="text-right text-white/30 text-[10px] tracking-wider uppercase py-3 px-4">Gold Partners</th>
                    <th className="text-right text-white/30 text-[10px] tracking-wider uppercase py-3 pl-4">Avg Occupancy</th>
                  </tr>
                </thead>
                <tbody>
                  {STATE_COVERAGE.map(s => (
                    <tr key={s.state} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 pr-4">
                        <span className="text-white/70 text-sm">{s.state}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-white/50 text-sm">{s.total.toLocaleString()}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-[#5FB8C2] rounded-full" style={{ width: `${(s.verified / s.total) * 100}%` }} />
                          </div>
                          <span className="text-white/50 text-sm w-12 text-right">{s.verified.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="text-[#D4A853] text-sm">{s.gold}</span>
                      </td>
                      <td className="text-right py-3 pl-4">
                        <span className={`text-sm ${s.occupancy >= 70 ? 'text-[#9EBD6E]' : s.occupancy >= 55 ? 'text-white/50' : 'text-white/30'}`}>{s.occupancy}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/15">
                    <td className="py-3 pr-4"><span className="text-white text-sm font-medium">Total</span></td>
                    <td className="text-right py-3 px-4"><span className="text-white text-sm">{totalHomestays.toLocaleString()}</span></td>
                    <td className="text-right py-3 px-4"><span className="text-white text-sm">{totalVerified.toLocaleString()}</span></td>
                    <td className="text-right py-3 px-4"><span className="text-[#D4A853] text-sm">{STATE_COVERAGE.reduce((s, d) => s + d.gold, 0)}</span></td>
                    <td className="text-right py-3 pl-4"><span className="text-[#9EBD6E] text-sm">{Math.round(STATE_COVERAGE.reduce((s, d) => s + d.occupancy, 0) / STATE_COVERAGE.length)}%</span></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Metrics */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="quality-metrics">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Rating Distribution */}
            <div className="border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="mb-6">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Quality Assurance</p>
                <h3 className="text-lg text-white font-light italic" style={playfair}>Rating Distribution</h3>
              </div>
              <div className="space-y-3">
                {QUALITY_METRICS.map(q => (
                  <div key={q.label} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-[#D4A853]" />
                        <span className="text-white/60 text-xs">{q.label}</span>
                      </div>
                      <span className="text-white/30 text-xs">{q.count.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4A853]/60 rounded-full" style={{ width: `${q.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 text-center">
                <p className="text-3xl text-white font-light" style={playfair}>4.2</p>
                <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Avg rating across platform</p>
              </div>
            </div>

            {/* Host Enablement */}
            <div className="lg:col-span-2 border border-white/8 rounded-xl p-6 bg-white/[0.02]">
              <div className="mb-6">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">Host Enablement</p>
                <h3 className="text-xl text-white font-light italic" style={playfair}>Impact Metrics</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { value: '2,840', label: 'Properties Photographed', icon: Eye },
                  { value: '1,420', label: 'Hosts Trained', icon: Users },
                  { value: '680', label: 'Digital Profiles Created', icon: Globe },
                  { value: '94%', label: 'Host Satisfaction', icon: Star },
                ].map(m => (
                  <div key={m.label} className="text-center border border-white/5 rounded-lg p-4">
                    <m.icon className="w-4 h-4 text-[#C4915C] mx-auto mb-2" />
                    <p className="text-xl text-white font-light" style={playfair}>{m.value}</p>
                    <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1 leading-tight">{m.label}</p>
                  </div>
                ))}
              </div>
              <div className="border border-white/5 rounded-lg p-5">
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-3">Revenue Impact</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl text-[#9EBD6E] font-light" style={playfair}>+42%</p>
                    <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Avg booking increase after verification</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl text-[#5FB8C2] font-light" style={playfair}>+67%</p>
                    <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Visibility boost after photography</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl text-[#C4915C] font-light" style={playfair}>+28%</p>
                    <p className="text-white/25 text-[9px] tracking-wider uppercase mt-1">Revenue growth for gold partners</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="analytics-cta">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#9EBD6E] text-[10px] tracking-[0.5em] uppercase mb-6">For State Tourism Departments</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-6" style={playfair}>
            This is what your state gets.
          </h2>
          <p className="text-white/40 text-sm leading-relaxed max-w-xl mx-auto mb-10">
            Real-time demand intelligence. Seasonal occupancy insights. Traveller origin mapping.
            Quality verification tracking. All of it — live, always-on, and built for policy-making.
          </p>
          <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20learn%20more%20about%20the%20State%20Analytics%20Dashboard%20for%20our%20tourism%20department."
            className="inline-flex items-center gap-3 bg-[#9EBD6E] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#b5d185] transition-colors"
            data-testid="analytics-cta-btn" target="_blank" rel="noopener noreferrer">
            Schedule a Walkthrough <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5 text-center">
        <Link to="/" className="text-white/20 text-xs tracking-wider hover:text-white/40 transition-colors uppercase">
          GoHimalayan &middot; State Partnership Program
        </Link>
      </footer>
    </div>
  );
}
